import printfulDoNotCreateStoreImage from 'assets/images/others/printful-do-not-create-store.png';
import printfulSelectExistingStoreImage from 'assets/images/others/printful-select-existing-store.png';
import { FunctionComponent, Suspense, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay/hooks';
import { Link } from 'src/components/widgets/buttons';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { Section } from 'src/components/widgets/section';
import { showLoadingAction } from 'src/shared/actions';
import { useAppState } from 'src/shared/appState';
import { backofficeUrl, getMessageFromGraphQLError, stringFormat, T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { CatalogListLoaderContent } from '../../../../widgets/catalogList/catalogListItemLoader';
import { FormControl } from '../../../../widgets/formControl';
import { SearchInput } from '../../../../widgets/input';
import { ContainerBase } from '../../../../widgets/layouts';
import { showNotification } from '../../../../widgets/notificationToaster';
import { selectProviderProductAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { productProvider_listItem$data } from '../../state/queries/__generated__/productProvider_listItem.graphql';
import ImportCatalogListQuery from './importCatalogList';
import SearchCategory from './searchCategory';

const SearchContainer = styled.div<{ hasCategory: boolean }>`
    display: grid;
    grid-gap: 30px;

    ${(props) =>
        props.hasCategory &&
        `
        grid-template-columns: 1fr 426px;
    `}

    ${(props) =>
        !props.hasCategory &&
        `
        grid-template-columns: 1fr;
    `}
`;

const PrintfulRecoveryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const PrintfulRecoverySteps = styled.ol`
    margin: 0;
    padding-left: 20px;

    li + li {
        margin-top: 8px;
    }
`;

const PrintfulStoreExamples = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const PrintfulStoreExample = styled.figure`
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PrintfulStoreImage = styled.img`
    width: 100%;
    max-height: 360px;
    object-fit: contain;
    object-position: top;
    border: 1px solid #dbe2e6;
    border-radius: 6px;
`;

const PrintfulStoreCaption = styled.figcaption`
    font-size: 13px;
    line-height: 18px;
`;

const ECOMMERCE_PLATFORM_NAMES: Record<string, string> = {
    BIG_COMMERCE: 'BigCommerce',
    SHOPIFY: 'Shopify',
    WOO_COMMERCE: 'WooCommerce'
};

const getErrorText = (error: unknown): string => {
    const values: string[] = [];
    const visited = new Set<object>();

    const collect = (value: unknown) => {
        if (typeof value === 'string') {
            values.push(value);
            return;
        }

        if (value === null || typeof value !== 'object' || visited.has(value)) return;

        visited.add(value);
        Object.getOwnPropertyNames(value).forEach((property) => {
            try {
                collect((value as Record<string, unknown>)[property]);
            } catch {
                // Some browser Error properties cannot be read. Their details are not needed here.
            }
        });
    };

    collect(error);
    return values.join(' ');
};

const isInvalidPrintfulRefreshTokenError = (error: unknown) =>
    /PrintfulCommonProductProvider\.RefreshToken|Refresh token is not valid/i.test(getErrorText(error));

const PrintfulRefreshTokenRecovery: FunctionComponent<{ platformName?: string }> = ({ platformName }) => {
    const storeDescription = platformName
        ? stringFormat(T._('the existing store connected to your {0} account', 'Admin'), platformName)
        : T._('your existing ecommerce store', 'Admin');

    return (
        <PrintfulRecoveryContent>
            <NotificationBanner
                type='ERROR'
                title={T._('Your Printful connection needs to be renewed.', 'Admin')}
                content={T._('Printful could not refresh the authorization for this account. Re-integrate Printful before importing a product.', 'Admin')}
            />
            <PrintfulRecoverySteps>
                <li>
                    <Link href={backofficeUrl('/integration/provider-configuration-page/printful')}>{T._('Open Printful integration settings', 'Admin')}</Link>{' '}
                    {T._('and remove the current Printful integration.', 'Admin')}
                </li>
                <li>
                    {stringFormat(
                        T._('Integrate Printful again. On the authorization page, select {0} from the store drop-down, then select Authorize.', 'Admin'),
                        storeDescription
                    )}
                </li>
            </PrintfulRecoverySteps>
            <PrintfulStoreExamples>
                <PrintfulStoreExample>
                    <PrintfulStoreImage src={printfulDoNotCreateStoreImage} alt={T._('Do not create a new Printful store during authorization', 'Admin')} />
                    <PrintfulStoreCaption>{T._('Do not create a new Printful store.', 'Admin')}</PrintfulStoreCaption>
                </PrintfulStoreExample>
                <PrintfulStoreExample>
                    <PrintfulStoreImage src={printfulSelectExistingStoreImage} alt={T._('Select the existing Printful store during authorization', 'Admin')} />
                    <PrintfulStoreCaption>{T._('Select the existing store that is connected to your ecommerce platform.', 'Admin')}</PrintfulStoreCaption>
                </PrintfulStoreExample>
            </PrintfulStoreExamples>
        </PrintfulRecoveryContent>
    );
};

const ImportCatalog: FunctionComponent<{}> = (props) => {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState<string | null>(null);
    const { state, dispatch } = useProductConfigurationState();
    const { dispatch: appDispatch } = useAppState();
    const changeStep = useProductConfigurationChangeStep();
    const relayEnvironment = useRelayEnvironment();
    const ecommercePlatform = state.ecommerceSalesChannel.find((platform) => platform !== 'NONE');
    const platformName = ecommercePlatform ? (ECOMMERCE_PLATFORM_NAMES[ecommercePlatform] ?? ecommercePlatform.replaceAll('_', ' ')) : undefined;

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    const handleProductClick = async (providerProduct: Omit<RelayFragment<productProvider_listItem$data>, ' $fragmentType'>) => {
        // Ensure the product is compatible with Zakeke

        try {
            appDispatch(showLoadingAction(true));

            const query = await fetchQuery(
                relayEnvironment,
                graphql`
                    query importCatalogCheckQuery($productID: String!, $provider: ProductProvider!) {
                        providerProduct(productID: $productID, provider: $provider) {
                            ...productProvider_product
                        }
                    }
                `,
                {
                    productID: providerProduct.id,
                    provider: state.provider
                }
            ).toPromise();

            dispatch(selectProviderProductAction(providerProduct)); // Select merchant product
            changeStep('import-configuration'); // Go to next page
        } catch (ex: any) {
            let message = getMessageFromGraphQLError(ex);

            if (message === 'Product not supported.') message = T._('Product is not supported by Zakeke, please choose another product.', 'Admin');

            showNotification({
                message,
                type: 'danger',
                dismiss: {
                    duration: 5000
                }
            });
        } finally {
            appDispatch(showLoadingAction(false));
        }
    };

    return (
        <ContainerBase>
            <Section>
                <CustomErrorBoundary
                    render={(error) =>
                        state.provider === 'PRINTFUL' && isInvalidPrintfulRefreshTokenError(error) ? (
                            <PrintfulRefreshTokenRecovery platformName={platformName} />
                        ) : (
                            <NotificationBanner type='ERROR' content={error?.message} />
                        )
                    }
                >
                    <FormControl>
                        <SearchContainer hasCategory={state.provider !== 'CUSTOM_CAT'}>
                            <SearchInput fullWidth placeholder={T._('Search by product name ...', 'Admin')} onSearch={handleSearch} />

                            {state.provider !== 'CUSTOM_CAT' && (
                                <Suspense fallback={null}>
                                    <SearchCategory provider={state.provider} value={category} onChange={(id) => setCategory(id)} />
                                </Suspense>
                            )}
                        </SearchContainer>
                    </FormControl>

                    <Suspense fallback={<CatalogListLoaderContent columns={{ xs: 1, md: 5, lg: 8 }} />}>
                        <ImportCatalogListQuery search={search} category={category} onProductClick={handleProductClick} />
                    </Suspense>
                </CustomErrorBoundary>
            </Section>
        </ContainerBase>
    );
};

export default ImportCatalog;
